import { useEffect, useMemo, useRef, useState } from "react"

// --- Hooks ---
import { useMenuNavigation } from "@/tiptap/hooks/use-menu-navigation"

// --- Lib ---
import { getElementOverflowPosition } from "@/tiptap/lib/tiptap-collab-utils"

// --- UI Primitives ---
import { Button } from "@/tiptap/ui-primitive/button"
import { Input } from "@/tiptap/ui-primitive/input"
import {
  Card,
  CardBody,
  CardHeader,
  CardItemGroup,
} from "@/tiptap/ui-primitive/card"

import { getFilteredEmojis } from "@/tiptap/ui/emoji-menu/emoji-menu-utils"

// --- Styles ---

export const EmojiMenuItem = props => {
  const { emoji, isSelected, onSelect, selector } = props
  const itemRef = useRef(null)

  useEffect(() => {
    const menuElement = document.querySelector(selector)
    if (!itemRef.current || !isSelected || !menuElement) return

    const overflow = getElementOverflowPosition(itemRef.current, menuElement)
    if (overflow === "top") {
      itemRef.current.scrollIntoView(true)
    } else if (overflow === "bottom") {
      itemRef.current.scrollIntoView(false)
    }
  }, [isSelected, selector])

  if (!emoji) return null

  return (
    <Button
      ref={itemRef}
      variant="ghost"
      data-active-state={isSelected ? "on" : "off"}
      onClick={() => onSelect(emoji)}>
      {emoji.fallbackImage ? (
        <img
          className="tiptap-button-emoji"
          src={emoji.fallbackImage}
          alt={emoji.name} />
      ) : (
        <span className="tiptap-button-emoji">{emoji.emoji}</span>
      )}
      <span className="tiptap-button-text">:{emoji.name}:</span>
    </Button>
  );
}

export const EmojiMenu = props => {
  const {
    emojis,
    onSelect,
    onClose,
    showSearch = false,
    selector = ".emoji-menu-list",
  } = props

  const containerRef = useRef(null)
  const searchRef = useRef(null)

  const [searchQuery, setSearchQuery] = useState("")

  const filteredEmojis = useMemo(() => {
    const filtered = getFilteredEmojis({ query: searchQuery, emojis })
    return filtered
  }, [emojis, searchQuery])

  const { selectedIndex } = useMenuNavigation({
    containerRef,
    query: searchQuery,
    items: filteredEmojis,
    onSelect,
    onClose,
  })

  const renderedItems = useMemo(() => {
    return filteredEmojis.map((emoji, index) => (
      <EmojiMenuItem
        key={emoji.name}
        emoji={emoji}
        index={index}
        isSelected={index === selectedIndex}
        onSelect={() => onSelect(emoji)}
        selector={selector} />
    ));
  }, [filteredEmojis, selectedIndex, onSelect, selector])

  return (
    <Card ref={containerRef}>
      {showSearch && (
        <CardHeader>
          <Input
            ref={searchRef}
            type="text"
            placeholder="Add a emoji reaction..."
            value={searchQuery}
            autoFocus
            onChange={(e) => setSearchQuery(e.target.value)}
            className="emoji-menu-search-input" />
        </CardHeader>
      )}
      {renderedItems.length && (
        <CardBody className="emoji-menu-list">
          <CardItemGroup>{renderedItems}</CardItemGroup>
        </CardBody>
      )}
    </Card>
  );
}
